import React, { FC, useEffect, useRef, useState } from 'react';
import GoogleMapReact, { Coords, MapTypeStyle } from 'google-map-react';
import useSupercluster from 'use-supercluster';
import { PointFeature } from 'supercluster';
import { Position } from 'geojson';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { featureCollection, point } from '@turf/helpers';
import bbox from '@turf/bbox';
import booleanContains from '@turf/boolean-contains';
import bboxPolygon from '@turf/bbox-polygon';
import boundsToBBox from '../services/bounds-to-bbox';
import { collect, Group, groupBy } from '../../services/collections';
import Job from '../../jobs/types/job';
import { OverlayJobListItemProperties, OverlayJobListProperties } from './overlay-job-list';
import { MarkerIconProperties } from './marker-icon';
import { JobWithCoordinates } from '../types/job-with-coordinates';
import InfoWindow from './info-window';
import Marker from './marker';
import './maps-view.scss';

export type MapsViewProperties = {
  googleMapsApiKey?: string;
  language: string;
  styles?: MapTypeStyle[];
  defaultCenter?: Coords;
  jobs: Job[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (bounds: BBox2d) => void;
  overlayListClassName?: string;
  overlayItemClassName?: string;
  List: React.ComponentType<OverlayJobListProperties>;
  Item: React.ComponentType<OverlayJobListItemProperties>;
  Icon: React.ComponentType<MarkerIconProperties>;
  order: number | undefined;
};

type PointProperties = { cluster: boolean; jobCount: number; key: string; jobs: Job[] };

const clusterMap = (properties: any) => ({
  jobCount: properties.jobCount,
});

const clusterReduce = (accumulator: any, properties: any) => {
  accumulator.jobCount += properties.jobCount;
  return accumulator;
};

const fitBounds = (map: any, toBounds: BBox2d) => {
  if (toBounds.some((element) => Number.isNaN(element) || !Number.isFinite(element))) {
    map.setZoom(0);
  } else {
    const [west, south, east, north] = toBounds;
    const latLngBounds = { east, north, south, west };

    map.fitBounds(latLngBounds, { bottom: 20, left: 50, right: 50, top: 20 });

    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      if (map.getZoom() > 11) {
        map.setZoom(11);
      }
    });
  }
};

const createPositionKey = ([lng, lat]: Position) => `${lng}_${lat}`;
const createBoundsKey = (bounds: BBox2d) => bounds.map((axis) => String(axis)).join('_');

const MapsView: FC<MapsViewProperties> = ({
  googleMapsApiKey,
  language,
  styles,
  defaultCenter,
  jobs,
  onChange,
  overlayListClassName,
  overlayItemClassName,
  List,
  Item,
  Icon,
  order,
}) => {
  const mapReference = useRef<any>();
  const [bounds, setBounds] = useState<BBox2d>();
  const [zoom, setZoom] = useState(7);
  const [showInfoWindow, setShowInfoWindow] = useState<string>();

  const all: JobWithCoordinates[] = jobs.flatMap((job) =>
    job.jobOpening.locations
      .filter((location) => typeof location.latitude === 'number' && typeof location.longitude === 'number')
      .map((location) => ({
        job,
        coordinates: [location.longitude!, location.latitude!],
      })),
  );

  const jobsGroupedByCoordinates: Group<JobWithCoordinates> = groupBy(all, (jobWithCoordinates) =>
    createPositionKey(jobWithCoordinates.coordinates),
  );

  const points: PointFeature<PointProperties>[] = collect(jobsGroupedByCoordinates, (key, jobsWithCoordinates) =>
    point(jobsWithCoordinates[0].coordinates, {
      cluster: false,
      jobCount: jobsWithCoordinates.length,
      key,
      jobs: jobsWithCoordinates.map(({ job }) => job),
    }),
  );

  const jobBounds = bbox(featureCollection(points)) as BBox2d;
  const jobBoundsKey = createBoundsKey(jobBounds);

  // fit map to job bounds conditionally
  useEffect(() => {
    const boundsPolygon = typeof bounds !== 'undefined' ? bboxPolygon(bounds) : undefined;
    const jobBoundsPolygon = bboxPolygon(jobBounds);
    if (
      typeof mapReference.current !== 'undefined' &&
      typeof boundsPolygon !== 'undefined' &&
      jobs.length > 0 &&
      !booleanContains(boundsPolygon, jobBoundsPolygon)
    ) {
      fitBounds(mapReference.current, jobBounds);
    }
  }, [jobBoundsKey, typeof mapReference.current, jobs.length]);

  // signal changed map bounds upstream
  useEffect(() => {
    if (typeof bounds !== 'undefined' && typeof onChange !== 'undefined') {
      onChange(bounds);
    }
  }, [(bounds && createBoundsKey(bounds)) || '']);

  // remember map and fit initially
  const onGoogleMapsLoaded = (map: any) => {
    mapReference.current = map;
    if (points.length > 0) {
      fitBounds(mapReference.current, jobBounds);
    }
  };

  const options = {
    radius: 150,
    maxZoom: 11,
    map: clusterMap,
    reduce: clusterReduce,
  };

  const { clusters, supercluster } = useSupercluster({ points, bounds, zoom, options });

  return (
    <div className="dvinci-job-filter dvinci-widget-maps-filter-view" style={{ order }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: googleMapsApiKey || '', language }}
        defaultCenter={defaultCenter}
        defaultZoom={0}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => onGoogleMapsLoaded(map)}
        onClick={() => setShowInfoWindow('')}
        onChange={({ zoom: updatedZoom, bounds: updatedBounds }) => {
          setZoom(updatedZoom);
          setBounds(boundsToBBox(updatedBounds));
        }}
        options={{
          gestureHandling: 'cooperative',
          fullscreenControl: false,
          styles,
        }}
      >
        {clusters.map((cluster) => {
          const [lng, lat] = cluster.geometry.coordinates;
          const { cluster: isCluster, jobCount } = cluster.properties;

          if (isCluster) {
            return (
              <Marker key={`cluster-${cluster.id}`} lng={lng} lat={lat}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
                <div
                  role="button"
                  onClick={() => {
                    const expansionZoom = Math.min(supercluster!.getClusterExpansionZoom(cluster.id! as number), 20);
                    mapReference.current?.setZoom(expansionZoom);
                    mapReference.current?.panTo({ lng, lat });
                    setShowInfoWindow('');
                  }}
                >
                  <Icon cluster count={jobCount} total={jobs.length} />
                </div>
              </Marker>
            );
          }

          const { key, jobs: clusterJobs } = cluster.properties as PointProperties;
          return (
            <Marker key={key} lat={lat} lng={lng}>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
              <div
                role="button"
                onClick={() => {
                  mapReference.current?.panTo({ lat, lng });
                  setShowInfoWindow(key);
                }}
              >
                <Icon count={jobCount} total={jobs.length} />
              </div>

              <InfoWindow show={showInfoWindow === key}>
                <List
                  className={overlayListClassName}
                  itemClassName={overlayItemClassName}
                  jobsAtMarkerLocation={clusterJobs}
                  markerKey={key}
                  Item={Item}
                />
              </InfoWindow>
            </Marker>
          );
        })}
      </GoogleMapReact>
    </div>
  );
};

export default MapsView;
